"use client";

import { useEffect, useRef, useState } from "react";

function extractNumericAndSuffix(value: string): {
  number: number | null;
  suffix: string;
} {
  const match = value.match(/([0-9]+)([^0-9]*)$/);
  if (!match) return { number: null, suffix: "" };
  const num = parseInt(match[1], 10);
  const suffix = match[2] ?? "";
  return { number: Number.isNaN(num) ? null : num, suffix };
}

export function AnimatedPrice({
  value,
  className,
}: {
  value: string;
  className?: string;
}) {
  const { number: targetNumber, suffix } = extractNumericAndSuffix(value);
  const [displayNumber, setDisplayNumber] = useState<number | null>(
    targetNumber
  );
  const prevNumberRef = useRef<number | null>(targetNumber);

  useEffect(() => {
    if (targetNumber === null) {
      setDisplayNumber(null);
      prevNumberRef.current = null;
      return;
    }

    const startNumber = prevNumberRef.current ?? targetNumber;
    const change = targetNumber - startNumber;
    if (change === 0) return;

    const duration = 500; // ms
    let rafId: number;
    const startTime = performance.now();

    const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

    const tick = (now: number) => {
      const elapsed = now - startTime;
      const t = Math.min(1, elapsed / duration);
      const eased = easeOutCubic(t);
      const current = Math.round(startNumber + change * eased);
      setDisplayNumber(current);
      if (t < 1) {
        rafId = requestAnimationFrame(tick);
      }
    };

    rafId = requestAnimationFrame(tick);
    prevNumberRef.current = targetNumber;

    return () => {
      cancelAnimationFrame(rafId);
    };
  }, [targetNumber]);

  if (targetNumber === null || displayNumber === null) {
    return <span className={className}>{value}</span>;
  }

  return (
    <span className={className}>
      {displayNumber}
      {suffix}
    </span>
  );
}
